import { RouteShell } from "@/components/route-shell";
import { downloadCsv } from "@/lib/download-csv";
import { trpc } from "@/lib/trpc";
import { formatDollars } from "@payrollmart-invoice/shared";
import { Link, createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, CheckCircle, Download, MinusCircle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/reports/")({
	component: ReconciliationPageRoute,
});

function ReconciliationPageRoute() {
	return (
		<RouteShell>
			<ReconciliationPage />
		</RouteShell>
	);
}

type PayrollSystem = "ADP" | "Paylocity" | "SurePayroll" | "Gusto";

// Status compares the customer's extraction-side source charges against the
// source charges captured on their invoices. It is a BOOKKEEPING reconciliation
// signal — NOT a profitability signal. A row can be "Reconciled" with a
// negative margin (the books tie out, but the customer is being undercharged).
// W-13 audit clarification: prior labels ("Matched"/"Discrepancy") read as
// margin commentary; renamed to "Reconciled"/"Out of sync" to set the right
// expectation. Backend enum stays matched/uninvoiced/discrepancy to avoid
// breaking the export CSV + downstream tests.
const STATUS_STYLES = {
	matched: {
		bg: "bg-[#f0fdf4]",
		text: "text-[#15803d]",
		icon: CheckCircle,
		label: "Reconciled",
		tooltip:
			"Source charges from extractions match the source charges captured on invoices within $0.01.",
	},
	uninvoiced: {
		bg: "bg-[#fffbeb]",
		text: "text-[#d97706]",
		icon: MinusCircle,
		label: "Uninvoiced",
		tooltip: "Extractions exist for this customer but no invoice has been generated yet.",
	},
	discrepancy: {
		bg: "bg-[#fef2f2]",
		text: "text-[#dc2626]",
		icon: AlertTriangle,
		label: "Out of sync",
		tooltip:
			"Extracted source charges and invoiced source charges differ by more than $0.01 — the invoice may have been generated from a different extraction set than what's currently in the database.",
	},
} as const;

function ReconciliationPage() {
	const [periodMonth, setPeriodMonth] = useState("");
	const [systemFilter, setSystemFilter] = useState<PayrollSystem | undefined>();
	const [statusFilter, setStatusFilter] = useState<
		"matched" | "uninvoiced" | "discrepancy" | undefined
	>();

	const { data, isLoading } = trpc.reports.reconciliation.useQuery(
		{
			periodMonth: periodMonth || undefined,
			payrollSystem: systemFilter,
		},
		{ throwOnError: true },
	);

	const filteredRows = data?.rows.filter((r) => !statusFilter || r.status === statusFilter) ?? [];

	const exportCsv = trpc.export.reconciliationCsv.useQuery(
		{ periodMonth: periodMonth || undefined, payrollSystem: systemFilter },
		{ enabled: false },
	);

	const handleExport = async () => {
		const result = await exportCsv.refetch();
		if (result.error) {
			toast.error(result.error.message || "Failed to export CSV");
			return;
		}
		if (!result.data) {
			toast.error("Export returned no data");
			return;
		}
		downloadCsv(result.data.csv, result.data.filename);
		toast.success("CSV downloaded");
	};

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-xl font-semibold text-[#1e1e20]">Reconciliation Report</h1>
					<p className="mt-1 text-sm text-[#6b7280]">
						Compare source charges against invoiced amounts per customer.
					</p>
				</div>
				<button
					type="button"
					onClick={handleExport}
					disabled={!data?.rows.length || exportCsv.isFetching}
					className="inline-flex items-center gap-1.5 rounded-md bg-[#4f46e5] px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-[#4338ca] disabled:opacity-50"
				>
					<Download className="h-4 w-4" strokeWidth={1.5} />
					{exportCsv.isFetching ? "Exporting..." : "Export CSV"}
				</button>
			</div>

			{/* Filters */}
			<div className="flex flex-wrap items-center gap-3">
				<input
					type="month"
					value={periodMonth}
					onChange={(e) => setPeriodMonth(e.target.value)}
					className="h-9 rounded border border-[#d1d5db] bg-white px-3 text-sm text-[#1e1e20] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
					title="Filter by period"
				/>
				<select
					value={systemFilter ?? ""}
					onChange={(e) =>
						setSystemFilter((e.target.value || undefined) as PayrollSystem | undefined)
					}
					className="h-9 rounded border border-[#d1d5db] bg-white px-3 text-sm text-[#1e1e20] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
				>
					<option value="">All Systems</option>
					<option value="ADP">ADP</option>
					<option value="Paylocity">Paylocity</option>
					<option value="SurePayroll">SurePayroll</option>
					<option value="Gusto">Gusto</option>
				</select>
				<select
					value={statusFilter ?? ""}
					onChange={(e) =>
						setStatusFilter(
							(e.target.value || undefined) as "matched" | "uninvoiced" | "discrepancy" | undefined,
						)
					}
					className="h-9 rounded border border-[#d1d5db] bg-white px-3 text-sm text-[#1e1e20] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
				>
					<option value="">All Statuses</option>
					<option value="matched">Reconciled</option>
					<option value="uninvoiced">Uninvoiced</option>
					<option value="discrepancy">Out of sync</option>
				</select>
			</div>

			{/* Summary cards */}
			{data?.summary && (
				<div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
					<SummaryCard
						label="Source Charges"
						value={formatDollars(data.summary.totalSource)}
						sub={`${data.summary.totalCustomers} customers`}
					/>
					<SummaryCard
						label="Invoiced"
						value={formatDollars(data.summary.totalInvoiced)}
						sub={`${data.summary.matchedCount} reconciled`}
					/>
					<SummaryCard
						label="Margin"
						value={formatDollars(data.summary.totalMargin)}
						sub={
							data.summary.totalInvoiced > 0
								? `${Math.round((data.summary.totalMargin / data.summary.totalInvoiced) * 100)}%`
								: "N/A"
						}
					/>
					<SummaryCard
						label="Issues"
						value={String(data.summary.uninvoicedCount + data.summary.discrepancyCount)}
						sub={`${data.summary.uninvoicedCount} uninvoiced, ${data.summary.discrepancyCount} out of sync`}
						alert={data.summary.uninvoicedCount + data.summary.discrepancyCount > 0}
					/>
				</div>
			)}

			{/* Table */}
			<div className="rounded-lg border border-[#e5e7eb] bg-white">
				{isLoading ? (
					<div className="space-y-0 divide-y divide-[#f3f4f6]">
						<div className="h-11 animate-pulse bg-[#f8f9fb]" />
						<div className="h-11 animate-pulse" />
						<div className="h-11 animate-pulse bg-[#f8f9fb]/50" />
					</div>
				) : filteredRows.length === 0 ? (
					<div className="py-12 text-center">
						<p className="text-sm font-semibold text-[#1e1e20]">No data found</p>
						<p className="mt-1 text-xs text-[#6b7280]">
							{periodMonth || systemFilter || statusFilter
								? "Try adjusting your filters."
								: "Process some documents to see reconciliation data."}
						</p>
					</div>
				) : (
					<div className="overflow-x-auto">
						<table className="w-full text-sm">
							<thead>
								<tr className="border-b-2 border-[#e5e7eb] bg-[#f8f9fb] text-left">
									<th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-[#6b7280]">
										Customer
									</th>
									<th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-[#6b7280]">
										System
									</th>
									<th
										className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-[#6b7280]"
										title="Sum of source charges extracted from payroll provider documents for this customer."
									>
										Extraction Source
									</th>
									<th
										className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-[#6b7280]"
										title="Sum of source charges captured on this customer's invoices. Reconciliation compares this column against Extraction Source."
									>
										Invoice Source
									</th>
									<th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-[#6b7280]">
										Invoiced
									</th>
									<th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-[#6b7280]">
										Margin
									</th>
									<th
										className="px-4 py-2.5 text-center text-xs font-medium uppercase tracking-wide text-[#6b7280]"
										title="Reconciliation compares Extraction Source against Invoice Source (within $0.01). Distinct from Margin (profitability)."
									>
										Reconciliation
									</th>
								</tr>
							</thead>
							<tbody>
								{filteredRows.map((row) => {
									const style = STATUS_STYLES[row.status];
									const Icon = style.icon;
									return (
										<tr
											key={row.customerId ?? row.customerName}
											className="border-b border-[#f3f4f6] last:border-0 transition-colors duration-150 hover:bg-[#f8f9fb]"
										>
											<td className="px-4 py-3">
												{row.customerId ? (
													<Link
														to="/customers/$customerId"
														params={{ customerId: row.customerId }}
														className="font-medium text-[#4f46e5] hover:underline"
													>
														{row.customerName}
													</Link>
												) : (
													<span className="text-[#9ca3af]">{row.customerName}</span>
												)}
											</td>
											<td className="px-4 py-3 text-[#6b7280]">{row.payrollSystem}</td>
											<td className="px-4 py-3 text-right tabular-nums font-medium text-[#1e1e20]">
												{formatDollars(row.sourceTotal)}
											</td>
											<td className="px-4 py-3 text-right tabular-nums font-medium text-[#1e1e20]">
												{row.invoiceCount > 0 ? formatDollars(row.invoiceSourceTotal) : "\u2014"}
											</td>
											<td className="px-4 py-3 text-right tabular-nums font-medium text-[#1e1e20]">
												{row.invoiceCount > 0 ? formatDollars(row.invoicedTotal) : "\u2014"}
											</td>
											<td className="px-4 py-3 text-right tabular-nums font-medium text-[#1e1e20]">
												{row.invoiceCount > 0 ? formatDollars(row.margin) : "\u2014"}
											</td>
											<td className="px-4 py-3 text-center">
												<span
													className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium ${style.bg} ${style.text}`}
													title={style.tooltip}
												>
													<Icon className="h-3 w-3" strokeWidth={1.5} />
													{style.label}
												</span>
											</td>
										</tr>
									);
								})}
							</tbody>
							<tfoot>
								<tr className="border-t-2 border-[#e5e7eb] bg-[#f8f9fb] font-semibold">
									<td className="px-4 py-2.5 text-sm text-[#1e1e20]" colSpan={2}>
										Totals ({filteredRows.length} customers)
									</td>
									<td className="px-4 py-2.5 text-right tabular-nums text-sm text-[#1e1e20]">
										{formatDollars(filteredRows.reduce((sum, r) => sum + r.sourceTotal, 0))}
									</td>
									<td className="px-4 py-2.5 text-right tabular-nums text-sm text-[#1e1e20]">
										{formatDollars(filteredRows.reduce((sum, r) => sum + r.invoiceSourceTotal, 0))}
									</td>
									<td className="px-4 py-2.5 text-right tabular-nums text-sm text-[#1e1e20]">
										{formatDollars(filteredRows.reduce((sum, r) => sum + r.invoicedTotal, 0))}
									</td>
									<td className="px-4 py-2.5 text-right tabular-nums text-sm text-[#1e1e20]">
										{formatDollars(filteredRows.reduce((sum, r) => sum + r.margin, 0))}
									</td>
									<td />
								</tr>
							</tfoot>
						</table>
					</div>
				)}
			</div>
		</div>
	);
}

function SummaryCard({
	label,
	value,
	sub,
	alert,
}: {
	label: string;
	value: string;
	sub: string;
	alert?: boolean;
}) {
	return (
		<div
			className={`rounded-lg border p-4 ${alert ? "border-[#d97706]/30 bg-[#fffbeb]/50" : "border-[#e5e7eb] bg-white"}`}
		>
			<p className="text-xs font-medium uppercase tracking-wide text-[#6b7280]">{label}</p>
			<p className="mt-1 text-lg font-semibold tabular-nums text-[#1e1e20]">{value}</p>
			<p className="mt-0.5 text-xs text-[#9ca3af]">{sub}</p>
		</div>
	);
}
